import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';

const EditorialRow = ({ index, title, subtitle, meta, to, href, sx = {} }) => {
  const theme = useTheme();

  const isExternal = Boolean(href);
  const Component = isExternal ? 'a' : Link;
  const linkProps = isExternal
    ? { href, target: '_blank', rel: 'noopener noreferrer' }
    : { to };

  return (
    <Box
      component={Component}
      {...linkProps}
      sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        flexDirection: { xs: 'column', sm: 'row' },
        py: 2.2,
        px: 2,
        mb: 1,
        borderRadius: '12px',
        border: `1px solid transparent`,
        borderBottom: `1px solid ${theme.palette.divider}`,
        textDecoration: 'none',
        transition: `all 220ms cubic-bezier(0.16, 1, 0.3, 1)`,
        gap: { xs: 1, sm: 2 },
        position: 'relative',
        '&:hover': {
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(229, 169, 60, 0.05)' : 'rgba(184, 134, 11, 0.05)',
          borderColor: theme.palette.mode === 'dark' ? 'rgba(229, 169, 60, 0.2)' : 'rgba(184, 134, 11, 0.2)',
          transform: 'translateX(4px)',
          boxShadow: theme.palette.mode === 'dark' ? '0 4px 16px rgba(0, 0, 0, 0.2)' : '0 4px 16px rgba(0, 0, 0, 0.04)',
          '& .row-title': {
            color: theme.palette.secondary.main,
          },
          '& .row-arrow': {
            transform: 'translateX(4px)',
            color: theme.palette.secondary.main,
          },
        },
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, minWidth: 0, flex: 1 }}>
        {index && (
          <Typography
            component="span"
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.74rem',
              color: theme.palette.secondary.main,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              display: 'inline-block',
            }}
          >
            {`[ ${index} ]`}
          </Typography>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            className="row-title"
            variant="h6"
            sx={{
              fontFamily: '"Newsreader", "Playfair Display", Georgia, serif',
              fontWeight: 600,
              fontSize: '1.25rem',
              lineHeight: 1.3,
              color: theme.palette.text.primary,
              transition: 'color 180ms ease',
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '0.88rem',
                mt: 0.35,
                lineHeight: 1.55,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, alignSelf: { xs: 'flex-start', sm: 'center' }, pl: { xs: 5, sm: 0 } }}>
        {meta && (
          <Typography
            component="span"
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.74rem',
              color: theme.palette.text.secondary,
              letterSpacing: '0.04em',
              whiteSpace: 'nowrap',
            }}
          >
            {meta}
          </Typography>
        )}
        <ArrowForwardIcon
          className="row-arrow"
          sx={{
            fontSize: 16,
            color: theme.palette.text.secondary,
            transition: 'transform 180ms ease, color 180ms ease',
          }}
        />
      </Box>
    </Box>
  );
};

export default EditorialRow;
